/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useMemo, useState } from 'react'

const WORDS = ['هیچ', 'یک', 'دو', 'سه', 'چهار', 'پنج', 'شش', 'هفت', 'هشت', 'نه', 'ده', 'یازده', 'دوازده']
const word = (n) => (WORDS[n] !== undefined ? WORDS[n] : n.toLocaleString('fa-IR'))
const TAG_LABEL = { posture: 'وضعیت بدن', shoulder: 'شانه', hip: 'لگن/ران', ankle: 'مچ/ساق', hamstring: 'همسترینگ', neck: 'گردن' }
const uniq = (a) => Array.from(new Set(a))

export default function SelfEval({ data }) {
  const [selected, setSelected] = useState(new Set())
  const [all, setAll] = useState(false)
  const [answers, setAnswers] = useState({})
  const [showResults, setShowResults] = useState(false)

  const visible = useMemo(() => data.items.map((it, i) => ({ it, i })).filter(({ it }) => all || it.tags.some((t) => selected.has(t))), [data.items, selected, all])
  const done = visible.filter(({ i }) => answers[i]).length
  const failed = visible.filter(({ i }) => answers[i] === 'fail')
  const pct = visible.length ? Math.round((done / visible.length) * 100) : 0

  const toggle = (tag) => {
    setAll(false)
    setSelected((s) => {
      const n = new Set(s)
      if (n.has(tag)) n.delete(tag)
      else n.add(tag)
      return n
    })
  }
  const pickAll = () => {
    setSelected(new Set())
    setAll(true)
  }
  const clear = () => {
    setSelected(new Set())
    setAll(false)
    setShowResults(false)
  }
  const reset = () => setAnswers({})

  const areas = new Map()
  let issues = []
  let nexts = []
  failed.forEach(({ it }) => {
    it.tags.map((t) => TAG_LABEL[t]).filter(Boolean).forEach((a) => areas.set(a, (areas.get(a) || 0) + 1))
    issues = issues.concat(it.issues)
    nexts = nexts.concat(it.next)
  })

  const targets = data.targets.filter((t) => t.tag && t.tag !== 'all' && t.tag !== 'clear')

  return (
    <section className="floating-card se-tests" id="home-tests">
      <h2 className="section-heading">{data.title}</h2>
      <p className="section-paragraph">{data.intro}</p>
      <div className="se-targets">
        <span className="se-targets__label">انتخاب ناحیه:</span>
        <div className="se-targets__wrap">
          {targets.map((t) => (
            <button type="button" key={t.tag} className={'tg' + (selected.has(t.tag) ? ' is-active' : '')} onClick={() => toggle(t.tag)}>
              {t.label}
            </button>
          ))}
          <button type="button" className={'tg tg--all' + (all ? ' is-active' : '')} onClick={pickAll}>
            همه
          </button>
          <button type="button" className="tg tg--clear" onClick={clear}>
            پاک‌کردن
          </button>
        </div>
      </div>
      {!showResults && visible.length > 0 && (
        <>
          <div className="se-progress">
            <div className="se-progress__bar">
              <div className="se-progress__fill" style={{ width: pct + '%' }} />
            </div>
            <div className="se-progress__meta">
              <span>{done.toLocaleString('fa-IR')}</span> / <span>{visible.length.toLocaleString('fa-IR')}</span> انجام‌شده
            </div>
          </div>
          <div className="se-list">
            {visible.map(({ it, i }) => (
              <article className="se-item" key={i} data-state={answers[i] || undefined}>
                <header className="se-item__head">
                  <div className="se-meta">
                    <h3 className="se-name">{it.title}</h3>
                    <div className="se-chips">
                      {it.chips.map((c) => (
                        <span className="chip" key={c}>
                          {c}
                        </span>
                      ))}
                    </div>
                  </div>
                  {it.tip && (
                    <button type="button" className="se-info" aria-label="راهنما" onClick={() => window.alert(it.tip)}>
                      ؟
                    </button>
                  )}
                </header>
                <div className="se-item__body">
                  <strong>چگونه:</strong>
                  <ol className="se-steps">
                    {it.steps.map((s, j) => (
                      <li key={j}>{s.replace(/^[۰-۹0-9]+[)\-.]\s*/, '')}</li>
                    ))}
                  </ol>
                  {it.obs.map((o, j) => (
                    <p key={j}>{o}</p>
                  ))}
                </div>
                <div className="se-actions">
                  <button type="button" className={'btn-pass' + (answers[i] === 'pass' ? ' is-active' : '')} onClick={() => setAnswers((a) => ({ ...a, [i]: 'pass' }))}>
                    طبیعی
                  </button>
                  <button type="button" className={'btn-fail' + (answers[i] === 'fail' ? ' is-active' : '')} onClick={() => setAnswers((a) => ({ ...a, [i]: 'fail' }))}>
                    نیاز به اصلاح
                  </button>
                </div>
              </article>
            ))}
          </div>
          <div className="se-actions-final">
            <button type="button" className="btn-primary" onClick={() => setShowResults(true)}>
              نمایش نتیجه
            </button>
            <button type="button" className="btn-ghost" onClick={reset}>
              شروع دوباره
            </button>
          </div>
        </>
      )}
      {showResults && (
        <aside className="se-total">
          <div className="se-total__head">نتیجه‌ی خودارزیابی</div>
          <div className="se-summary">
            <span>{`${word(done)} تست از ${word(visible.length)} تست انجام‌شده`}</span>
            <span className={'se-pill' + (failed.length === 0 ? ' is-ok' : '')}>{failed.length === 0 ? 'در هیچ موردی نیاز به اصلاح نداشتید.' : `در ${word(failed.length)} مورد نیاز به اصلاح داشتید.`}</span>
          </div>
          {failed.length > 0 && (
            <div className="se-total__grid">
              <div className="total-col">
                <h4>نواحی</h4>
                <ul className="plain">
                  {Array.from(areas.entries()).map(([k, v]) => (
                    <li key={k}>{`${k} (${v.toLocaleString('fa-IR')})`}</li>
                  ))}
                </ul>
              </div>
              <div className="total-col">
                <h4>مشکلات احتمالی</h4>
                <ul className="plain">
                  {uniq(issues).map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
              <div className="total-col">
                <h4>قدم بعدی</h4>
                <ul className="plain">
                  {uniq(nexts).map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          <div className="se-actions-final">
            <button type="button" className="btn-ghost" onClick={() => setShowResults(false)}>
              بازگشت به تست‌ها
            </button>
          </div>
        </aside>
      )}
    </section>
  )
}
